import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Volume2, VolumeX, SkipForward, GraduationCap, ChevronDown, Mic, Settings, MessageSquare } from 'lucide-react';
import { voiceTutorChat, describeNoteImages, transcribeAndRefineAudio, sendTutorSessionEmail, VoiceTutorMessage, VoiceTutorSettings, DEFAULT_TUTOR_SETTINGS } from '../services/geminiService';
import { Language } from '../types';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';

interface Props {
    nodeId: string;
    nodeTitle: string;
    nodeContent: string;
    nodeImages?: { base64: string; mimeType: string }[];
    selectedText?: string;
    onClearSelectedText?: () => void;
    hoverContext?: string;
    language: Language;
    onClose: () => void;
    onSessionEnd?: (messages: VoiceTutorMessage[]) => void;
    onPhaseChange?: (speaking: boolean) => void;
    onHighlightImage?: (index: number | null) => void;
    defaultShowSettings?: boolean;
}

type Phase = 'initializing' | 'ai-speaking' | 'user-ready' | 'user-speaking' | 'ai-thinking' | 'error';
const SILENCE_TIMEOUT = 2500;

// Voice options per language
const VOICES = {
    vietnamese: [
        { id: 'vi-male-b',   label: 'Nam' },
        { id: 'vi-female-a', label: 'Nữ' },
    ],
    english: [
        { id: 'en-male-a',   label: 'Male 1' },
        { id: 'en-male-b',   label: 'Male 2' },
        { id: 'en-female-a', label: 'Female 1' },
        { id: 'en-female-b', label: 'Female 2' },
    ],
};

const VALID_VOICE_IDS = new Set(['vi-male-b', 'vi-female-a', 'en-male-a', 'en-male-b', 'en-female-a', 'en-female-b']);

const STYLES = [
    { id: 'polite',   emoji: '🎓', label: 'Lịch sự',     labelEn: 'Polite' },
    { id: 'friendly', emoji: '😊', label: 'Thân thiện',   labelEn: 'Friendly' },
    { id: 'maytao',   emoji: '👊', label: 'Mày Tao',      labelEn: 'Casual' },
    { id: 'brutal',   emoji: '💀', label: 'Thẳng Thắn',   labelEn: 'Brutal' },
];

const SPEEDS = [
    { value: 0.85, label: '0.85×', desc: 'Chậm' },
    { value: 1.0,  label: '1.0×',  desc: 'Bình thường' },
    { value: 1.1,  label: '1.1×',  desc: 'Nhanh' },
    { value: 1.25, label: '1.25×', desc: 'Rất nhanh' },
];

const STORAGE_KEY = 'sb_voice_tutor_settings';

const loadSaved = (fallbackLanguage: string): { lang: 'vietnamese' | 'english'; settings: VoiceTutorSettings } => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            const lang: 'vietnamese' | 'english' = parsed.lang ?? (fallbackLanguage === 'vietnamese' ? 'vietnamese' : 'english');
            const settings: VoiceTutorSettings = { ...DEFAULT_TUTOR_SETTINGS, ...parsed.settings };
            if (!VALID_VOICE_IDS.has(settings.voiceId)) settings.voiceId = lang === 'vietnamese' ? 'vi-male-b' : 'en-male-a';
            return { lang, settings };
        }
    } catch {}
    const lang = fallbackLanguage === 'vietnamese' ? 'vietnamese' : 'english';
    return { lang, settings: { ...DEFAULT_TUTOR_SETTINGS, voiceId: lang === 'vietnamese' ? 'vi-male-b' : 'en-male-a' } };
};

export const VoiceTutorModal: React.FC<Props> = ({ nodeId, nodeTitle, nodeContent, nodeImages, selectedText, onClearSelectedText, hoverContext, language, onClose, onSessionEnd, onPhaseChange, onHighlightImage, defaultShowSettings }) => {
    const saved = useRef(loadSaved(language)).current;
    const [tutorLanguage, setTutorLanguage] = useState<'vietnamese' | 'english'>(saved.lang);
    const isVi = tutorLanguage === 'vietnamese';
    const [messages, setMessages] = useState<VoiceTutorMessage[]>([]);
    const [phase, setPhase] = useState<Phase>('initializing');
    const [interimText, setInterimText] = useState('');
    const [muted, setMuted] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [showSettings, setShowSettings] = useState(!!defaultShowSettings);
    const [settings, setSettings] = useState<VoiceTutorSettings>(saved.settings);
    const [silenceProgress, setSilenceProgress] = useState(0);
    const [errorMsg, setErrorMsg] = useState('');
    const [aiSubtitle, setAiSubtitle] = useState('');

    const recognitionRef = useRef<any>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const audioCtxRef = useRef<AudioContext | null>(null);
    const ampRafRef = useRef<number | null>(null);
    const blobRef = useRef<HTMLDivElement>(null);
    const micAnalyserRef = useRef<AnalyserNode | null>(null);
    const micCtxRef = useRef<AudioContext | null>(null);
    const micStreamRef = useRef<MediaStream | null>(null);
    const micRafRef = useRef<number | null>(null);
    const micWaveCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const finalTranscriptRef = useRef('');
    const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const silenceAnimRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const geminiSilenceIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const subtitleTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const pendingSubtitleRef = useRef('');
    const scrollRef = useRef<HTMLDivElement>(null);
    const phaseRef = useRef<Phase>('initializing');
    const mutedRef = useRef(false);
    const messagesRef = useRef<VoiceTutorMessage[]>([]);
    const settingsRef = useRef(settings);
    const tutorLangRef = useRef(tutorLanguage);
    const imageContextRef = useRef<string | undefined>(undefined);
    const selectedTextRef = useRef<string | undefined>(undefined);
    const hoverContextRef = useRef<string | undefined>(undefined);
    const [imageContextReady, setImageContextReady] = useState(!nodeImages || nodeImages.length === 0);
    const openedAtRef = useRef(Date.now());

    const handleClose = useCallback(() => {
        const currentMessages = messagesRef.current;
        const userTurns = currentMessages.filter(m => m.role === 'user').length;
        if (userTurns >= 2 && onSessionEnd) {
            onSessionEnd(currentMessages);
        }
        onClose();
    }, [onClose, onSessionEnd]);

    useEffect(() => {
        posthog.capture('voice_tutor_opened', { role: settings.role, style: settings.style, language: tutorLanguage });
        return () => {
            const duration_sec = Math.round((Date.now() - openedAtRef.current) / 1000);
            posthog.capture('voice_tutor_closed', { message_count: messagesRef.current.length, duration_sec });
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    phaseRef.current = phase;
    mutedRef.current = muted;
    messagesRef.current = messages;
    settingsRef.current = settings;
    tutorLangRef.current = tutorLanguage;
    selectedTextRef.current = (selectedText && selectedText.length >= 10) ? selectedText : undefined;
    hoverContextRef.current = (!selectedText || selectedText.length < 10) && hoverContext && hoverContext.length >= 5 ? hoverContext : undefined;

    useEffect(() => { onPhaseChange?.(phase === 'ai-speaking'); }, [phase]);
    useEffect(() => {
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ lang: tutorLanguage, settings })); } catch {}
    }, [tutorLanguage, settings]);

    const scrollToBottom = () => setTimeout(() => scrollRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);

    const stopAmplitudeLoop = () => {
        if (ampRafRef.current) { cancelAnimationFrame(ampRafRef.current); ampRafRef.current = null; }
        document.documentElement.style.removeProperty('--vt-amp');
        audioCtxRef.current?.close().catch(() => {});
        audioCtxRef.current = null;
    };

    const stopAudio = () => {
        stopAmplitudeLoop();
        if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ''; audioRef.current = null; }
    };

    const playAudioBase64 = useCallback((base64: string, mimeType = 'audio/mpeg'): Promise<void> => {
        return new Promise((resolve) => {
            stopAudio();
            if (mutedRef.current) { resolve(); return; }
            const binary = atob(base64);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
            const blob = new Blob([bytes], { type: mimeType });
            const url = URL.createObjectURL(blob);
            const audio = new Audio(url);
            audioRef.current = audio;

            // Web Audio API — read amplitude at 60fps, write to CSS var for neon glow sync
            try {
                const ctx = new AudioContext();
                audioCtxRef.current = ctx;
                const analyser = ctx.createAnalyser();
                analyser.fftSize = 128;
                analyser.smoothingTimeConstant = 0.6;
                ctx.createMediaElementSource(audio).connect(analyser);
                analyser.connect(ctx.destination);
                const data = new Uint8Array(analyser.frequencyBinCount);
                const loop = () => {
                    analyser.getByteFrequencyData(data);
                    const amp = data.reduce((a, b) => a + b, 0) / data.length / 255;
                    document.documentElement.style.setProperty('--vt-amp', amp.toFixed(3));
                    // Drive blob scale directly — no React re-render
                    if (blobRef.current) blobRef.current.style.transform = `scale(${(1 + amp * 0.18).toFixed(3)})`;
                    ampRafRef.current = requestAnimationFrame(loop);
                };
                ctx.resume().then(() => { ampRafRef.current = requestAnimationFrame(loop); });
            } catch { /* fallback: CSS animation handles it */ }

            // Subtitle sync — distribute words evenly across actual audio duration
            audio.addEventListener('loadedmetadata', () => {
                const text = pendingSubtitleRef.current;
                if (!text || audio.duration <= 0) return;
                const words = text.trim().split(/\s+/).filter(Boolean);
                if (words.length === 0) return;
                const msPerWord = Math.max(150, (audio.duration * 1000) / words.length);
                let idx = 1;
                setAiSubtitle(words[0]);
                if (subtitleTimerRef.current) clearInterval(subtitleTimerRef.current);
                subtitleTimerRef.current = setInterval(() => {
                    setAiSubtitle(words.slice(0, idx + 1).join(' '));
                    idx++;
                    if (idx >= words.length) { clearInterval(subtitleTimerRef.current!); subtitleTimerRef.current = null; }
                }, msPerWord);
            });

            const cleanup = () => {
                stopAmplitudeLoop();
                URL.revokeObjectURL(url);
                audioRef.current = null;
                if (subtitleTimerRef.current) { clearInterval(subtitleTimerRef.current); subtitleTimerRef.current = null; }
                setAiSubtitle('');
                pendingSubtitleRef.current = '';
            };
            audio.onended = () => { cleanup(); resolve(); };
            audio.onerror = () => { cleanup(); resolve(); };
            audio.play().catch(() => { cleanup(); resolve(); });
        });
    }, []);

    const speakFallback = useCallback((text: string): Promise<void> => {
        return new Promise((resolve) => {
            window.speechSynthesis?.cancel();
            if (mutedRef.current || !window.speechSynthesis) { resolve(); return; }
            const words = text.trim().split(/\s+/).filter(Boolean);
            const utter = new SpeechSynthesisUtterance(text);
            utter.lang = isVi ? 'vi-VN' : 'en-US';
            utter.rate = settingsRef.current.speed;
            // Word-level subtitle sync via onboundary
            setAiSubtitle(words[0] || '');
            let wordIdx = 0;
            utter.onboundary = (e: SpeechSynthesisEvent) => {
                if (e.name === 'word') {
                    wordIdx = Math.min(wordIdx + 1, words.length);
                    setAiSubtitle(words.slice(0, wordIdx).join(' '));
                }
            };
            const clearSub = () => { setAiSubtitle(''); pendingSubtitleRef.current = ''; };
            utter.onend = () => { clearSub(); resolve(); };
            utter.onerror = () => { clearSub(); resolve(); };
            const trySpeak = () => {
                const voices = window.speechSynthesis.getVoices();
                const preferred = voices.find(v => v.lang.startsWith(isVi ? 'vi' : 'en') && (v.name.includes('Neural') || v.name.includes('Enhanced'))) || voices.find(v => v.lang.startsWith(isVi ? 'vi' : 'en'));
                if (preferred) utter.voice = preferred;
                window.speechSynthesis.speak(utter);
            };
            if (window.speechSynthesis.getVoices().length > 0) trySpeak();
            else window.speechSynthesis.onvoiceschanged = () => trySpeak();
        });
    }, [isVi]);

    const clearSilenceTimer = () => {
        if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }
        if (silenceAnimRef.current) { clearInterval(silenceAnimRef.current); silenceAnimRef.current = null; }
        setSilenceProgress(0);
    };

    const startSilenceTimer = useCallback(() => {
        clearSilenceTimer();
        const start = Date.now();
        silenceAnimRef.current = setInterval(() => setSilenceProgress(Math.min(100, (Date.now() - start) / SILENCE_TIMEOUT * 100)), 50);
        silenceTimerRef.current = setTimeout(() => {
            clearSilenceTimer();
            if (phaseRef.current === 'user-speaking') recognitionRef.current?.stop();
        }, SILENCE_TIMEOUT);
    }, []);

    const stopMicVisualizer = () => {
        if (micRafRef.current) { cancelAnimationFrame(micRafRef.current); micRafRef.current = null; }
        micCtxRef.current?.close().catch(() => {});
        micCtxRef.current = null;
        micStreamRef.current?.getTracks().forEach(t => t.stop());
        micStreamRef.current = null;
        micAnalyserRef.current = null;
        if (blobRef.current) blobRef.current.style.transform = '';
        const canvas = micWaveCanvasRef.current;
        if (canvas) { const c = canvas.getContext('2d'); c?.clearRect(0, 0, canvas.width, canvas.height); }
    };

    const runMicVisualLoop = () => {
        const analyser = micAnalyserRef.current;
        if (!analyser) return;
        micRafRef.current = requestAnimationFrame(runMicVisualLoop);
        const data = new Uint8Array(analyser.fftSize);
        analyser.getByteTimeDomainData(data);
        // RMS amplitude 0–1
        let sum = 0;
        for (let i = 0; i < data.length; i++) { const v = (data[i] - 128) / 128; sum += v * v; }
        const rms = Math.sqrt(sum / data.length);
        // Scale orb with mic amplitude (mirrors AI speaking orb animation)
        if (blobRef.current) blobRef.current.style.transform = `scale(${Math.min(1.4, 1 + rms * 1.2).toFixed(3)})`;
        // Draw waveform bars to canvas
        const canvas = micWaveCanvasRef.current;
        if (!canvas) return;
        const ctx2d = canvas.getContext('2d');
        if (!ctx2d) return;
        const { width, height } = canvas;
        ctx2d.clearRect(0, 0, width, height);
        const BAR_COUNT = 28;
        const barW = Math.max(2, Math.floor(width / BAR_COUNT) - 1);
        const gap = 1;
        const totalW = (barW + gap) * BAR_COUNT - gap;
        const offsetX = Math.floor((width - totalW) / 2);
        const step = Math.max(1, Math.floor(data.length / BAR_COUNT));
        for (let i = 0; i < BAR_COUNT; i++) {
            let s = 0;
            for (let j = 0; j < step; j++) {
                const v = (data[Math.min(i * step + j, data.length - 1)] - 128) / 128;
                s += v * v;
            }
            const barRms = Math.sqrt(s / step);
            const barH = Math.max(3, Math.min(height, barRms * height * 5 + 3));
            ctx2d.fillStyle = barRms > 0.02 ? 'rgba(239,68,68,0.85)' : 'rgba(180,180,190,0.45)';
            ctx2d.fillRect(offsetX + (barW + gap) * i, (height - barH) / 2, barW, barH);
        }
    };

    const startMicVisualizer = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            micStreamRef.current = stream;
            const ctx = new AudioContext();
            micCtxRef.current = ctx;
            const source = ctx.createMediaStreamSource(stream);
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 256;
            analyser.smoothingTimeConstant = 0.5;
            source.connect(analyser);
            micAnalyserRef.current = analyser;
            runMicVisualLoop();
        } catch { /* mic already in use or denied — orb CSS animation handles it */ }
    };

    const startListeningRealtime = useCallback(() => {
        // @ts-ignore
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) { setPhase('error'); setErrorMsg(isVi ? 'Dùng Chrome/Edge cho mic.' : 'Use Chrome or Edge.'); return; }
        finalTranscriptRef.current = '';
        clearSilenceTimer();
        const rec = new SR();
        rec.continuous = true; rec.interimResults = true;
        rec.lang = isVi ? 'vi-VN' : 'en-US';
        rec.onresult = (e: any) => {
            let interim = '';
            for (let i = e.resultIndex; i < e.results.length; i++) {
                if (e.results[i].isFinal) finalTranscriptRef.current += e.results[i][0].transcript + ' ';
                else interim += e.results[i][0].transcript;
            }
            setInterimText(finalTranscriptRef.current + interim);
            if (finalTranscriptRef.current.trim() || interim.trim()) startSilenceTimer();
        };
        rec.onend = () => {
            const text = finalTranscriptRef.current.trim();
            setInterimText(''); finalTranscriptRef.current = ''; clearSilenceTimer(); stopMicVisualizer();
            if (text && phaseRef.current === 'user-speaking') handleUserMessage(text);
            else if (phaseRef.current === 'user-speaking') setPhase('user-ready');
        };
        rec.onerror = () => { setInterimText(''); finalTranscriptRef.current = ''; clearSilenceTimer(); stopMicVisualizer(); if (phaseRef.current === 'user-speaking') setPhase('user-ready'); };
        recognitionRef.current = rec;
        rec.start();
        setPhase('user-speaking');
        startMicVisualizer(); // separate stream just for waveform visualization
    }, [isVi, startSilenceTimer]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleUserMessage = useCallback((text: string) => {
        const userMsg: VoiceTutorMessage = { role: 'user', text };
        setMessages(prev => { const next = [...prev, userMsg]; scrollToBottom(); return next; });
        fetchAIImperative([...messagesRef.current, userMsg], text);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const startListeningGemini = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const chunks: Blob[] = [];
            const recorder = new MediaRecorder(stream);
            mediaRecorderRef.current = recorder;

            // Time-domain amplitude — more reliable for silence detection than frequency data
            const ctx = new AudioContext();
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 512;
            analyser.smoothingTimeConstant = 0.8;
            ctx.createMediaStreamSource(stream).connect(analyser);
            const data = new Uint8Array(analyser.fftSize);

            // Store analyser for visualizer loop
            micAnalyserRef.current = analyser;
            runMicVisualLoop();

            let hasSpoken = false;
            let silenceMs = 0;
            const GEMINI_SILENCE_MS = 2500;
            const SPEAK_THRESHOLD = 0.02; // raised from 0.008 — fan/ambient noise stays below this

            setSilenceProgress(0);

            geminiSilenceIntervalRef.current = setInterval(() => {
                analyser.getByteTimeDomainData(data);
                // Deviation from center (128) normalized to 0-1
                const amp = data.reduce((sum, v) => sum + Math.abs(v - 128), 0) / data.length / 128;
                if (amp > SPEAK_THRESHOLD) {
                    hasSpoken = true;
                    silenceMs = 0;
                    setSilenceProgress(0);
                } else if (hasSpoken) {
                    silenceMs += 100;
                    setSilenceProgress(Math.min(100, (silenceMs / GEMINI_SILENCE_MS) * 100));
                    if (silenceMs >= GEMINI_SILENCE_MS) {
                        if (geminiSilenceIntervalRef.current) { clearInterval(geminiSilenceIntervalRef.current); geminiSilenceIntervalRef.current = null; }
                        recorder.stop();
                    }
                }
            }, 100);

            recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
            recorder.onstop = async () => {
                if (geminiSilenceIntervalRef.current) { clearInterval(geminiSilenceIntervalRef.current); geminiSilenceIntervalRef.current = null; }
                stopMicVisualizer();
                ctx.close();
                stream.getTracks().forEach(t => t.stop());
                mediaRecorderRef.current = null;
                setSilenceProgress(0);

                if (!hasSpoken || phaseRef.current !== 'user-speaking') {
                    setInterimText('');
                    if (phaseRef.current === 'user-speaking') setPhase('user-ready');
                    return;
                }

                setInterimText(isVi ? 'Đang xử lý...' : 'Processing...');
                try {
                    const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
                    const base64 = await new Promise<string>((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                            const result = reader.result as string;
                            resolve(result.split(',')[1]);
                        };
                        reader.onerror = reject;
                        reader.readAsDataURL(blob);
                    });
                    const text = await transcribeAndRefineAudio(base64, tutorLangRef.current === 'vietnamese' ? 'vietnamese' : 'english');
                    setInterimText('');
                    if (text.trim() && phaseRef.current === 'user-speaking') handleUserMessage(text.trim());
                    else if (phaseRef.current === 'user-speaking') setPhase('user-ready');
                } catch {
                    setInterimText('');
                    if (phaseRef.current === 'user-speaking') setPhase('user-ready');
                }
            };

            recorder.start();
            setPhase('user-speaking');
        } catch {
            setPhase('error');
            setErrorMsg(isVi ? 'Không thể truy cập mic.' : 'Cannot access microphone.');
        }
    }, [isVi, handleUserMessage]);

    const startListening = useCallback(() => {
        if (settingsRef.current.sttMode === 'gemini') { startListeningGemini(); return; }
        startListeningRealtime();
    }, [startListeningRealtime, startListeningGemini]);

    const [lastTtsSource, setLastTtsSource] = React.useState<string | undefined>(undefined);

    const fetchAIImperative = useCallback(async (history: VoiceTutorMessage[], userMsg: string) => {
        setPhase('ai-thinking');
        try {
            const { text, audioBase64, audioMimeType, highlightImage, ttsSource } = await voiceTutorChat(nodeTitle, nodeContent, history, userMsg, tutorLangRef.current, settingsRef.current, imageContextRef.current, selectedTextRef.current, hoverContextRef.current);
            setLastTtsSource(ttsSource);
            console.log('[VoiceTutor] TTS source:', ttsSource, '| audio:', audioBase64 ? `${Math.round(audioBase64.length / 1024)}KB` : 'null', '| mime:', audioMimeType);
            if (ttsSource === 'cloud') {
                Sentry.addBreadcrumb({ category: 'voice_tutor', message: 'Gemini TTS failed — using Cloud TTS fallback', level: 'warning', data: { audioMimeType, audioSize: audioBase64?.length } });
            } else if (!audioBase64) {
                Sentry.captureMessage('VoiceTutor: no audio returned from either TTS', { level: 'warning', tags: { feature: 'voice_tutor' }, extra: { ttsSource, audioMimeType, nodeTitle } });
            }
            const aiMsg: VoiceTutorMessage = { role: 'ai', text };
            setMessages(prev => { const next = [...prev, aiMsg]; scrollToBottom(); return next; });
            onHighlightImage?.(highlightImage ?? null);
            setPhase('ai-speaking');
            pendingSubtitleRef.current = text;
            if (audioBase64) await playAudioBase64(audioBase64, audioMimeType);
            else await speakFallback(text);
            if (phaseRef.current === 'ai-speaking') {
                onHighlightImage?.(null);
                setPhase('user-ready');
                setTimeout(() => { if (phaseRef.current === 'user-ready') startListening(); }, 400);
            }
        } catch (err: any) {
            Sentry.captureException(err, {
                tags: { feature: 'voice_tutor', step: 'fetchAI' },
                extra: { nodeTitle, ttsSource: lastTtsSource, phase: phaseRef.current, errorMessage: err?.message },
            });
            setPhase('error');
            setErrorMsg(isVi ? 'Không thể kết nối AI.' : 'Could not reach AI.');
        }
    }, [nodeTitle, nodeContent, language, playAudioBase64, speakFallback, startListening, isVi, onHighlightImage]);

    // On first mount: describe images then start greeting
    useEffect(() => {
        if (!nodeImages || nodeImages.length === 0) {
            fetchAIImperative([], '');
        } else {
            describeNoteImages(nodeImages).then(descs => {
                if (descs.length > 0) imageContextRef.current = descs.map((d, i) => `Image ${i + 1}: ${d}`).join('\n');
            }).catch(() => {}).finally(() => {
                setImageContextReady(true);
                fetchAIImperative([], '');
            });
        }
        return () => {
            stopAudio(); stopMicVisualizer(); window.speechSynthesis?.cancel();
            recognitionRef.current?.stop();
            mediaRecorderRef.current?.stop();
            if (geminiSilenceIntervalRef.current) { clearInterval(geminiSilenceIntervalRef.current); geminiSilenceIntervalRef.current = null; }
            clearSilenceTimer();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // When user navigates to a different note: silently update image context, don't restart conversation
    const prevNodeIdRef = useRef(nodeId);
    useEffect(() => {
        if (nodeId === prevNodeIdRef.current) return;
        prevNodeIdRef.current = nodeId;
        // Clear stale image context immediately so next message doesn't reference wrong images
        imageContextRef.current = undefined;
        onHighlightImage?.(null);
        if (!nodeImages || nodeImages.length === 0) return;
        describeNoteImages(nodeImages).then(descs => {
            if (descs.length > 0) imageContextRef.current = descs.map((d, i) => `Image ${i + 1}: ${d}`).join('\n');
        }).catch(() => {});
    }, [nodeId, nodeImages, onHighlightImage]);

    const skipAI = () => {
        stopAudio(); window.speechSynthesis?.cancel();
        if (phaseRef.current === 'ai-speaking') { setPhase('user-ready'); setTimeout(() => { if (phaseRef.current === 'user-ready') startListening(); }, 200); }
    };
    const toggleMute = () => {
        const next = !muted; setMuted(next); mutedRef.current = next;
        if (next) { stopAudio(); window.speechSynthesis?.cancel(); }
        if (phase === 'ai-speaking') { setPhase('user-ready'); setTimeout(() => { if (phaseRef.current === 'user-ready') startListening(); }, 300); }
    };
    const stopUserSpeaking = () => {
        recognitionRef.current?.stop();
        if (geminiSilenceIntervalRef.current) { clearInterval(geminiSilenceIntervalRef.current); geminiSilenceIntervalRef.current = null; }
        if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
    };

    const handleOrbTap = () => {
        if (phase === 'user-speaking') stopUserSpeaking();
        else if (phase === 'ai-speaking') skipAI();
        else if (phase === 'user-ready') startListening();
        else if (phase === 'error') { setPhase('initializing'); setErrorMsg(''); fetchAIImperative(messages, ''); }
    };


    const micActive = phase === 'user-speaking';
    const aiSpeaking = phase === 'ai-speaking';
    const isLoading = phase === 'initializing' || phase === 'ai-thinking';
    const RADIUS = 29; const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

    const voiceList = isVi ? VOICES.vietnamese : VOICES.english;
    const currentStyle = STYLES.find(s => s.id === settings.style) ?? STYLES[1];

    const blobStyle = (speaking: boolean, listening: boolean, loading: boolean, error: boolean): React.CSSProperties => ({
        width: 56, height: 56,
        background: listening
            ? 'radial-gradient(circle at 40% 35%, #fca5a5, #f87171, #dc2626)'
            : speaking
                ? 'radial-gradient(circle at 38% 32%, #ddd6fe, #8b5cf6, #6d28d9)'
                : loading
                    ? 'radial-gradient(circle at 38% 32%, #c7d2fe, #818cf8, #4f46e5)'
                    : error
                        ? 'radial-gradient(circle at 40% 35%, #fca5a5, #f87171, #dc2626)'
                        : 'radial-gradient(circle at 38% 32%, #ede9fe, #a78bfa, #7c3aed)',
        boxShadow: speaking
            ? '0 0 18px rgba(139,92,246,0.7), 0 0 40px rgba(139,92,246,0.35), 0 4px 20px rgba(109,40,217,0.4)'
            : listening
                ? '0 0 12px rgba(239,68,68,0.5), 0 4px 15px rgba(239,68,68,0.3)'
                : '0 4px 20px rgba(109,40,217,0.35)',
        animation: listening
            ? 'blobListen 1.8s ease-in-out infinite'
            : speaking
                ? 'blobSpeak 2s ease-in-out infinite'
                : 'blobIdle 5s ease-in-out infinite',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background 0.5s, box-shadow 0.4s',
        flexShrink: 0,
    });

    const widget = (
        <>
        <style>{`
            @keyframes blobSpeak {
                0%   { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
                20%  { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; }
                40%  { border-radius: 50% 30% 60% 40% / 60% 50% 40% 50%; }
                60%  { border-radius: 40% 70% 30% 60% / 30% 40% 70% 50%; }
                80%  { border-radius: 70% 30% 55% 45% / 50% 65% 35% 55%; }
                100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
            }
            @keyframes blobListen {
                0%   { border-radius: 50% 50% 50% 50%; }
                33%  { border-radius: 58% 42% 52% 48% / 54% 48% 52% 46%; }
                66%  { border-radius: 44% 56% 46% 54% / 46% 54% 48% 52%; }
                100% { border-radius: 50% 50% 50% 50%; }
            }
            @keyframes blobIdle {
                0%   { border-radius: 50% 50% 50% 50%; }
                25%  { border-radius: 58% 42% 54% 46% / 52% 46% 54% 48%; }
                50%  { border-radius: 46% 54% 44% 56% / 44% 56% 46% 54%; }
                75%  { border-radius: 52% 48% 58% 42% / 56% 44% 52% 48%; }
                100% { border-radius: 50% 50% 50% 50%; }
            }
        `}</style>

        {/* ── AI subtitle overlay — YouTube-style, bottom center ── */}
        {aiSpeaking && aiSubtitle && (
            <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-[590] pointer-events-none select-none px-4 max-w-lg w-full flex justify-center">
                <div className="bg-black/75 text-white text-sm px-5 py-2.5 rounded-xl leading-relaxed text-center backdrop-blur-sm">
                    {aiSubtitle}
                </div>
            </div>
        )}

        <div className="fixed bottom-6 right-6 z-[600] flex flex-col items-end gap-2 select-none">

            {/* ── Expanded chat panel ── */}
            {expanded && (
                <div className="w-80 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 flex flex-col overflow-hidden animate-in slide-in-from-bottom-2 duration-200"
                    style={{ maxHeight: '60vh' }}>
                    {/* Panel header */}
                    <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 dark:border-slate-800 shrink-0">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shrink-0">
                            <GraduationCap size={12} className="text-white" />
                        </div>
                        <p className="flex-1 text-xs font-semibold text-gray-700 dark:text-gray-200 truncate">{nodeTitle || 'AI Tutor'}</p>
                        <button onClick={() => setShowSettings(s => !s)} className={`p-1.5 rounded-lg transition-colors ${showSettings ? 'bg-violet-100 dark:bg-violet-900/40 text-violet-600' : 'hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400'}`}>
                            <Settings size={13} />
                        </button>
                        <button onClick={toggleMute} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400 transition-colors">
                            {muted ? <VolumeX size={13} /> : <Volume2 size={13} />}
                        </button>
                        <button onClick={() => setExpanded(false)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400 transition-colors">
                            <ChevronDown size={13} />
                        </button>
                        <button onClick={handleClose} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400 transition-colors">
                            <X size={13} />
                        </button>
                    </div>

                    {/* Settings panel */}
                    {showSettings && (
                        <div className="px-4 py-3 bg-gray-50 dark:bg-slate-800/60 border-b border-gray-100 dark:border-slate-700 shrink-0 space-y-3">
                            {/* Language toggle */}
                            <div>
                                <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">Ngôn ngữ / Language</p>
                                <div className="flex gap-1">
                                    {(['vietnamese', 'english'] as const).map(lang => (
                                        <button key={lang}
                                            onClick={() => {
                                                setTutorLanguage(lang);
                                                // Auto-switch voice to match new language
                                                const wrongLang = lang === 'vietnamese' ? !settings.voiceId.startsWith('vi') : !settings.voiceId.startsWith('en');
                                                if (wrongLang) setSettings(s => ({ ...s, voiceId: lang === 'vietnamese' ? 'vi-male-b' : 'en-male-a' }));
                                                // Clear history so AI starts fresh in new language (no cross-language confusion)
                                                stopAudio(); window.speechSynthesis?.cancel();
                                                const newGreeting = lang === 'vietnamese'
                                                    ? 'Xin chào! Bạn muốn hỏi gì về bài này?'
                                                    : 'Hey! What would you like to ask about?';
                                                setMessages([{ role: 'ai', text: newGreeting }]);
                                                setPhase('user-ready');
                                                setTimeout(() => { if (phaseRef.current === 'user-ready') startListening(); }, 400);
                                            }}
                                            className={`flex-1 py-1 rounded-lg text-xs font-semibold transition-colors ${tutorLanguage === lang ? 'bg-violet-600 text-white' : 'bg-white dark:bg-slate-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-slate-600 hover:border-violet-400'}`}>
                                            {lang === 'vietnamese' ? '🇻🇳 Tiếng Việt' : '🇺🇸 English'}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {/* Voice */}
                            <div>
                                <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">{isVi ? 'Giọng' : 'Voice'}</p>
                                <div className="flex flex-wrap gap-1">
                                    {voiceList.map(v => (
                                        <button key={v.id}
                                            onClick={() => setSettings(s => ({ ...s, voiceId: v.id }))}
                                            className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${settings.voiceId === v.id ? 'bg-violet-600 text-white' : 'bg-white dark:bg-slate-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-slate-600 hover:border-violet-400'}`}>
                                            {v.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {/* Speed */}
                            <div>
                                <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">{isVi ? 'Tốc độ' : 'Speed'}</p>
                                <div className="flex gap-1">
                                    {SPEEDS.map(sp => (
                                        <button key={sp.value}
                                            onClick={() => setSettings(s => ({ ...s, speed: sp.value }))}
                                            className={`flex-1 py-1 rounded-lg text-xs font-medium transition-colors ${settings.speed === sp.value ? 'bg-violet-600 text-white' : 'bg-white dark:bg-slate-700 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-slate-600 hover:border-violet-400'}`}>
                                            {sp.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            {/* Style */}
                            <div>
                                <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1.5">{isVi ? 'Phong cách' : 'Style'}</p>
                                <div className="flex gap-1">
                                    {STYLES.map(st => (
                                        <button key={st.id}
                                            onClick={() => setSettings(s => ({ ...s, style: st.id }))}
                                            title={isVi ? st.label : st.labelEn}
                                            className={`flex-1 py-1.5 rounded-lg text-sm transition-colors flex items-center justify-center gap-1 ${settings.style === st.id ? 'bg-violet-600 text-white' : 'bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 hover:border-violet-400'}`}>
                                            <span>{st.emoji}</span>
                                        </button>
                                    ))}
                                </div>
                                <p className="text-[10px] text-center text-gray-400 mt-1">{isVi ? currentStyle.label : currentStyle.labelEn}</p>
                            </div>
                        </div>
                    )}

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2 min-h-0">
                        {messages.map((msg, i) => (
                            <div key={i} className={`flex gap-1.5 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                                {msg.role === 'ai' && (
                                    <div className="w-5 h-5 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shrink-0 mt-0.5">
                                        <GraduationCap size={10} className="text-white" />
                                    </div>
                                )}
                                <div className={`max-w-[85%] px-3 py-2 rounded-xl text-xs leading-relaxed ${
                                    msg.role === 'ai' ? 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-tl-sm' : 'bg-violet-600 text-white rounded-tr-sm'
                                }`}>{msg.text}</div>
                            </div>
                        ))}
                        {micActive && interimText && (
                            <div className="flex flex-row-reverse">
                                <div className="max-w-[85%] px-3 py-2 rounded-xl rounded-tr-sm text-xs leading-relaxed bg-violet-200/60 dark:bg-violet-900/40 text-violet-800 dark:text-violet-200 italic">{interimText}</div>
                            </div>
                        )}
                        {isLoading && (
                            <div className="flex gap-1.5">
                                <div className="w-5 h-5 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shrink-0">
                                    <GraduationCap size={10} className="text-white" />
                                </div>
                                <div className="px-3 py-2.5 bg-gray-100 dark:bg-slate-800 rounded-xl rounded-tl-sm flex items-center gap-1">
                                    {[0, 150, 300].map(d => <span key={d} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />)}
                                </div>
                            </div>
                        )}
                        {phase === 'error' && <p className="text-xs text-red-500 text-center py-1">{errorMsg}</p>}
                        <div ref={scrollRef} />
                    </div>

                    {/* Status */}
                    <div className="px-4 py-2 border-t border-gray-100 dark:border-slate-800 shrink-0 flex items-center justify-between">
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                            {micActive ? (isVi ? '🎙 Đang nghe...' : '🎙 Listening...')
                                : aiSpeaking ? (isVi ? '💬 AI đang nói...' : '💬 AI speaking...')
                                    : isLoading ? (isVi ? 'Đang suy nghĩ...' : 'Thinking...') : ''}
                        </p>
                        <div className="flex items-center gap-1">
                            {lastTtsSource && (
                                <span title={`TTS: ${lastTtsSource}`} className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${lastTtsSource === 'gemini' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400' : lastTtsSource === 'cloud' ? 'bg-orange-100 text-orange-600 dark:bg-orange-900/40 dark:text-orange-400' : 'bg-gray-100 text-gray-500'}`}>
                                    {lastTtsSource === 'gemini' ? 'G' : lastTtsSource === 'cloud' ? 'C' : '?'}
                                </span>
                            )}
                            {aiSpeaking && <button onClick={skipAI} className="p-1 text-gray-400 hover:text-gray-600"><SkipForward size={13} /></button>}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Selected text context indicator ── */}
            {selectedText && selectedText.length >= 10 && (
                <div className="max-w-[260px] flex items-center gap-2 px-3 py-1.5 rounded-xl bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 animate-in slide-in-from-bottom-2 duration-200 pointer-events-none select-none">
                    <span className="text-amber-400 shrink-0 text-[11px]">👁</span>
                    <div className="min-w-0">
                        <p className="text-[9px] font-semibold text-amber-600 dark:text-amber-500 leading-tight">{isVi ? 'AI thấy đoạn bạn đang chọn' : 'AI sees your selection'}</p>
                        <p className="text-[9px] text-amber-500 dark:text-amber-600 truncate leading-tight">{selectedText.slice(0, 50)}{selectedText.length > 50 ? '…' : ''}</p>
                    </div>
                </div>
            )}
            {/* ── Hover context indicator (shown only when no selection active) ── */}
            {(!selectedText || selectedText.length < 10) && hoverContext && hoverContext.length >= 5 && (
                <div className="max-w-[260px] flex items-center gap-2 px-3 py-1.5 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 animate-in slide-in-from-bottom-2 duration-200 pointer-events-none select-none">
                    <span className="text-blue-400 shrink-0 text-[11px]">📍</span>
                    <div className="min-w-0">
                        <p className="text-[9px] font-semibold text-blue-600 dark:text-blue-400 leading-tight">{isVi ? 'AI thấy đoạn chuột đang ở' : 'AI sees where you\'re reading'}</p>
                        <p className="text-[9px] text-blue-500 dark:text-blue-500 truncate leading-tight">{hoverContext.slice(0, 50)}{hoverContext.length > 50 ? '…' : ''}</p>
                    </div>
                </div>
            )}

            {/* ── Orb + waveform + controls ── */}
            <div className="flex flex-col items-center gap-1.5">

                {/* Waveform canvas — sits above orb when mic is active */}
                {micActive && (
                    <canvas
                        ref={micWaveCanvasRef}
                        width={160}
                        height={28}
                        className="rounded-full"
                        style={{ background: 'rgba(239,68,68,0.07)', display: 'block' }}
                    />
                )}

                {/* Interim speech text — inline above orb in collapsed mode */}
                {!expanded && micActive && interimText && (
                    <div className="max-w-[200px] bg-white dark:bg-slate-800 rounded-xl shadow border border-gray-200 dark:border-slate-700 px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 italic text-center">
                        {interimText}
                    </div>
                )}

                {/* Orb — clean, no badge overlays */}
                <button onClick={handleOrbTap} className="relative flex items-center justify-center" style={{ width: 60, height: 60 }}>
                    {/* Silence countdown arc */}
                    {micActive && silenceProgress > 0 && (
                        <svg className="absolute inset-0 -rotate-90" width={60} height={60}>
                            <circle cx={30} cy={30} r={RADIUS} fill="none" stroke="rgba(239,68,68,0.2)" strokeWidth={3} />
                            <circle cx={30} cy={30} r={RADIUS} fill="none" stroke="rgb(239,68,68)" strokeWidth={3}
                                strokeDasharray={CIRCUMFERENCE} strokeDashoffset={CIRCUMFERENCE - (silenceProgress / 100) * CIRCUMFERENCE}
                                strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.05s linear' }} />
                        </svg>
                    )}

                    {/* Animated blob */}
                    <div ref={blobRef} style={blobStyle(aiSpeaking, micActive, isLoading, phase === 'error')}>
                        {micActive && <Mic size={20} className="text-white drop-shadow" />}
                        {isLoading && (
                            <div className="flex items-end gap-0.5">
                                {[0,120,240].map(d => <span key={d} className="w-1 h-1 bg-white/80 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />)}
                            </div>
                        )}
                    </div>
                </button>

                {/* Controls row below orb */}
                <div className="flex items-center gap-2">
                    {/* Style emoji */}
                    <span className="text-base leading-none" title={isVi ? currentStyle.label : currentStyle.labelEn}>
                        {currentStyle.emoji}
                    </span>

                    {/* Chat toggle */}
                    <button onClick={() => setExpanded(e => !e)}
                        className={`w-8 h-8 rounded-full flex items-center justify-center shadow border transition-colors ${expanded ? 'bg-violet-600 border-violet-500 text-white' : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-400 hover:text-violet-500 dark:hover:text-violet-400'}`}>
                        <MessageSquare size={14} />
                    </button>

                    {/* Close */}
                    <button onClick={(e) => { e.stopPropagation(); handleClose(); }}
                        className="w-8 h-8 rounded-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 shadow flex items-center justify-center text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors">
                        <X size={14} />
                    </button>
                </div>
            </div>
        </div>
        </>
    );

    return createPortal(widget, document.body);
};
